import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { type MenuOption } from '@saviynt/design-system';

import {
  PERSONALIZATION_DEFAULT_CONFIG,
  VIOLTIONS_LIST_SORT_ACTIONS,
  VIOLTIONS_LIST_SORT_FIELDS,
} from '../../../shared/constants';
import { useInvertedLocalize, usePersonalization } from '../../../shared/hooks';
import { type SortState } from '../data-layer/violationsTabTypes';
import { violationsTabActions } from '../data-store/violationsTabSlice';

/**
 * A custom hook that manages sorting functionality for violations list.
 * It handles two-level sorting menu state and interactions.
 *
 * @returns {Object} An object containing the following properties:
 *   @returns {MenuOption[]} fields - Array of available sort fields with their selection states
 *   @returns {string | undefined} fieldName - Currently selected field label
 *   @returns {MenuOption[]} actions - Array of available sort actions for the selected field
 *   @returns {string | undefined} actionName - Currently selected action label
 *   @returns {(fieldValue: string) => void} handleFieldClick - Callback function to handle field selection
 *   @returns {(actionValue: string) => void} handleActionClick - Callback function to handle action selection
 *
 * @example
 * const {
 *   fields,
 *   fieldName,
 *   actions,
 *   actionName,
 *   handleFieldClick,
 *   handleActionClick
 * } = useGetSortMenu();
 */
const useGetSortMenu = (currentSort: SortState | null) => {
  const l = useInvertedLocalize();
  const dispatch = useDispatch();
  const [menu1, setMenu1] = useState([] as MenuOption[]);
  const [menu2, setMenu2] = useState([] as MenuOption[]);
  const { getPersonlization } = usePersonalization();

  const defaultSort = useMemo(() => {
    const personalization = getPersonlization();

    return PERSONALIZATION_DEFAULT_CONFIG.saveSort &&
      personalization &&
      personalization.sort
      ? personalization.sort
      : {
          value: VIOLTIONS_LIST_SORT_FIELDS.UPDATEDATE.value,
          order: VIOLTIONS_LIST_SORT_ACTIONS.NEWTOOLD.value,
        };
  }, [getPersonlization]);

  useEffect(() => {
    let defaultField = defaultSort.value;
    let defaultAction = defaultSort.order;

    if (currentSort !== null) {
      defaultField = currentSort.value;
      defaultAction = currentSort.order;
    }

    const fields = Object.values(VIOLTIONS_LIST_SORT_FIELDS);

    setMenu1(
      fields.map((field) => ({
        label: l(field.label),
        value: field.value,
        isChecked: field.value === defaultField,
      }))
    );

    const actionArray =
      fields.find((field) => field.value === defaultField)?.options ?? [];

    setMenu2(
      actionArray.map((action) => ({
        label: l(VIOLTIONS_LIST_SORT_ACTIONS[action].label),
        value: VIOLTIONS_LIST_SORT_ACTIONS[action].value,
        isChecked: VIOLTIONS_LIST_SORT_ACTIONS[action].value === defaultAction,
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Should not update on `currentSort`, one time exectution
  }, [l, defaultSort]);

  useEffect(() => {
    const activeField = menu1.find((field) => field.isChecked)?.value;
    const activeAction = menu2.find((action) => action.isChecked)?.value;

    dispatch(
      violationsTabActions.setSortInAction(
        activeField && activeAction
          ? {
              value: activeField,
              order: activeAction,
            }
          : null
      )
    );
  }, [menu1, menu2, dispatch]);

  const handleFieldClick = useCallback(
    (fieldValue: string) => {
      setMenu1((prev) =>
        prev.map((field) => ({
          ...field,
          isChecked: field.value === fieldValue,
        }))
      );

      const actionArray =
        Object.values(VIOLTIONS_LIST_SORT_FIELDS).find(
          (field) => field.value === fieldValue
        )?.options ?? [];

      setMenu2(
        actionArray.map((action, index) => ({
          label: l(VIOLTIONS_LIST_SORT_ACTIONS[action].label),
          value: VIOLTIONS_LIST_SORT_ACTIONS[action].value,
          isChecked: index === 0,
        }))
      );
    },
    [l]
  );

  const handleActionClick = useCallback((actionValue: string) => {
    setMenu2((prev) =>
      prev.map((field) => ({
        ...field,
        isChecked: field.value === actionValue,
      }))
    );
  }, []);

  return {
    fields: menu1,
    fieldName: menu1.find((field) => field.isChecked)?.label,
    actions: menu2,
    actionName: menu2.find((action) => action.isChecked)?.label,
    handleFieldClick,
    handleActionClick,
  };
};

// eslint-disable-next-line import/prefer-default-export
export { useGetSortMenu };
